import type { SeriesInputValues, SeriesPoint } from '@/lib/tradingview/datafeeds/series-sources'
import type { ChartAnalyticsMetric } from '@/types/dashboard'
import { Exchange } from '@/types/generated/common/types'
import {
  CandleFamily,
  type CandleList,
  type ChartDataRequest,
  CrossExchangeInstrumentType,
} from '@/types/generated/public_api'

/**
 * Request building and response folding for the "(All exchanges)" studies.
 *
 * These studies send the same `ChartDataRequest` as every other chart fetch,
 * but with `crossExchange` set — which tells the API that `symbol` names an
 * ASSET (`BTC`), not an instrument, and that the `CANDLE_FAMILY_AGGREGATE_*`
 * columns should be folded from every matching leg. Everything here is pure so
 * the venue/type toggles and the unit switches are testable without a chart.
 */

export type CrossExchangeVenue = {
  /** Study input id — one bool checkbox per venue in TV's settings dialog. */
  inputId: string
  label: string
  exchange: Exchange
}

/**
 * Venues that publish `derivative_metrics` rows, i.e. the ones an open-interest
 * or funding aggregate can actually see. CME (`EXCHANGE_GLBX`) is in the list:
 * its outright futures do publish open interest, in contracts rather than in a
 * quantity, and the API rescales them by the contract size before folding.
 */
export const CROSS_EXCHANGE_DERIVATIVE_VENUES: CrossExchangeVenue[] = [
  { inputId: 'venueBybit', label: 'Bybit', exchange: Exchange.EXCHANGE_BYBIT },
  { inputId: 'venueDeribit', label: 'Deribit', exchange: Exchange.EXCHANGE_DERIBIT },
  { inputId: 'venueHyperliquid', label: 'Hyperliquid', exchange: Exchange.EXCHANGE_HYPERLIQUID },
  { inputId: 'venueDerive', label: 'Derive', exchange: Exchange.EXCHANGE_DERIVE },
  { inputId: 'venueLighter', label: 'Lighter', exchange: Exchange.EXCHANGE_LIGHTER },
  { inputId: 'venueGlbx', label: 'CME', exchange: Exchange.EXCHANGE_GLBX },
]

/**
 * Venues a volume aggregate can see. Superset of the derivative list: spot-only
 * venues trade the asset without ever reporting open interest, and Nasdaq
 * carries the spot ETFs that belong in a full CVD.
 *
 * Only venues we actually ingest are listed. Selecting a subset sends those
 * exchanges verbatim, and an exchange with no instruments in the cache
 * resolves to no legs at all — a 404, not an empty series. That rules out
 * Binance (no venue service exists in `oems_v2`) and NYSE (not a configured
 * Databento dataset; `equity_underlying` maps no ticker there either).
 * Coinbase stays: its service is real and its instruments are in the cache,
 * so it resolves legs and contributes zero only while the spot trade feed is
 * dark.
 */
export const CROSS_EXCHANGE_VOLUME_VENUES: CrossExchangeVenue[] = [
  ...CROSS_EXCHANGE_DERIVATIVE_VENUES,
  { inputId: 'venueCoinbase', label: 'Coinbase', exchange: Exchange.EXCHANGE_COINBASE },
  { inputId: 'venueUpbit', label: 'Upbit', exchange: Exchange.EXCHANGE_UPBIT },
  { inputId: 'venueXnas', label: 'Nasdaq (ETFs)', exchange: Exchange.EXCHANGE_XNAS },
]

export type CrossExchangeScopeInput = {
  inputId: string
  label: string
  instrumentType: CrossExchangeInstrumentType
}

/** Instrument types a CVD can span. Funding is perpetual-only by definition. */
export const CROSS_EXCHANGE_VOLUME_SCOPES: CrossExchangeScopeInput[] = [
  {
    inputId: 'includeSpot',
    label: 'Spot',
    instrumentType: CrossExchangeInstrumentType.CROSS_EXCHANGE_INSTRUMENT_TYPE_SPOT,
  },
  {
    inputId: 'includePerpetual',
    label: 'Perpetuals',
    instrumentType: CrossExchangeInstrumentType.CROSS_EXCHANGE_INSTRUMENT_TYPE_PERPETUAL,
  },
  {
    inputId: 'includeDatedFuture',
    label: 'Futures',
    instrumentType: CrossExchangeInstrumentType.CROSS_EXCHANGE_INSTRUMENT_TYPE_DATED_FUTURE,
  },
  {
    inputId: 'includeEtf',
    label: 'ETFs',
    instrumentType: CrossExchangeInstrumentType.CROSS_EXCHANGE_INSTRUMENT_TYPE_ETF,
  },
]

/** Open interest exists on perpetuals and dated futures — never on spot. */
export const CROSS_EXCHANGE_OI_SCOPES: CrossExchangeScopeInput[] =
  CROSS_EXCHANGE_VOLUME_SCOPES.filter(
    (scope) =>
      scope.instrumentType ===
        CrossExchangeInstrumentType.CROSS_EXCHANGE_INSTRUMENT_TYPE_PERPETUAL ||
      scope.instrumentType ===
        CrossExchangeInstrumentType.CROSS_EXCHANGE_INSTRUMENT_TYPE_DATED_FUTURE
  )

/**
 * A bool study input, defaulting to `true` when TV has not supplied a value.
 * TV probes `main` once with an empty input context to discover variables, so
 * an absent value means "not asked yet", not "unchecked".
 */
function isChecked(inputs: SeriesInputValues | undefined, inputId: string): boolean {
  const value = inputs?.[inputId]
  return value === undefined ? true : value === true || value === 'true'
}

/**
 * Checked venues, as the API's empty-means-all list. Everything checked sends
 * `[]` so the request stays stable as venues are added server-side; nothing
 * checked also sends `[]`, because the only other reading of "no venues" is an
 * empty chart, and a user who unticks the last box meant to reset, not to
 * blank the study.
 */
export function selectedExchanges(
  inputs: SeriesInputValues | undefined,
  venues: CrossExchangeVenue[]
): Exchange[] {
  const picked = venues.filter((venue) => isChecked(inputs, venue.inputId))
  if (picked.length === 0 || picked.length === venues.length) return []
  return picked.map((venue) => venue.exchange)
}

/**
 * Checked instrument types, ALWAYS listed explicitly — unlike the venue list,
 * this one never collapses to the empty "all" form. `[]` means every type the
 * API knows, which for an open-interest study would drag spot and ETF legs
 * into a read that can only report zero for them. The set is a closed enum, so
 * there is no forward-compatibility to buy by leaving it empty.
 *
 * Unticking every box falls back to the study's own full list, matching the
 * venue toggles: the last untick reads as a reset, not as a blank chart.
 */
export function selectedInstrumentTypes(
  inputs: SeriesInputValues | undefined,
  scopes: CrossExchangeScopeInput[]
): CrossExchangeInstrumentType[] {
  const picked = scopes.filter((scope) => isChecked(inputs, scope.inputId))
  return (picked.length === 0 ? scopes : picked).map((scope) => scope.instrumentType)
}

export type CrossExchangeRequestOptions = {
  /** Base asset, e.g. `BTC`. The API uppercases and trims it again. */
  asset: string
  timeframeSeconds: number
  fromSec: number
  toSec: number
  family: CandleFamily
  exchanges: Exchange[]
  instrumentTypes: CrossExchangeInstrumentType[]
}

/**
 * `exchange` is `EXCHANGE_UNSPECIFIED` on purpose — the request spans venues,
 * and the API ignores the field on this path. `CANDLE_FAMILY_CORE` is NOT
 * requested: an asset has no single price, so the aggregate leaves price OHLC
 * empty and asking for it would only widen the read.
 */
export function buildCrossExchangeRequest(options: CrossExchangeRequestOptions): ChartDataRequest {
  return {
    symbol: options.asset.trim().toUpperCase(),
    exchange: Exchange.EXCHANGE_UNSPECIFIED,
    timeframe: options.timeframeSeconds,
    from: Math.floor(options.fromSec).toString(),
    to: Math.floor(options.toSec).toString(),
    families: [options.family],
    crossExchange: {
      exchanges: options.exchanges,
      instrumentTypes: options.instrumentTypes,
    },
  }
}

/** Families the three studies request, named so the descriptors read plainly. */
export const CROSS_EXCHANGE_FAMILIES = {
  openInterest: CandleFamily.CANDLE_FAMILY_AGGREGATE_OI,
  funding: CandleFamily.CANDLE_FAMILY_AGGREGATE_FUNDING,
  cvd: CandleFamily.CANDLE_FAMILY_AGGREGATE_CVD,
} as const

function numeric(value: number | string | undefined): number | null {
  if (value === undefined || value === null) return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** Pull one closing column out of a folded CandleList as `SeriesPoint`s. */
function pointsFromColumn(
  candles: CandleList,
  metric: ChartAnalyticsMetric,
  column: Array<number | string>
): SeriesPoint[] {
  const points: SeriesPoint[] = []
  for (let i = 0; i < candles.ticks.length; i++) {
    const timeSec = numeric(candles.ticks[i])
    const value = numeric(column[i])
    if (timeSec === null || value === null) continue
    points.push({ timeSec, values: { [metric]: value } })
  }
  return points
}

export type OpenInterestMeasure = 'base' | 'quote'

export function crossExchangeOpenInterestPoints(
  candles: CandleList,
  metric: ChartAnalyticsMetric,
  measure: OpenInterestMeasure
): SeriesPoint[] {
  const column =
    measure === 'quote' ? candles.openInterestQuoteClose : candles.openInterestBaseClose
  return pointsFromColumn(candles, metric, column ?? [])
}

export type FundingMeasure = 'rate' | 'apr'

export function crossExchangeFundingPoints(
  candles: CandleList,
  metric: ChartAnalyticsMetric,
  measure: FundingMeasure
): SeriesPoint[] {
  const column = measure === 'apr' ? candles.fundingAprClose : candles.fundingRateClose
  return pointsFromColumn(candles, metric, column ?? [])
}

export type CvdMeasure = 'base' | 'quote'

/**
 * Running sum of the folded per-bucket net buy volume, rebased so the first
 * plotted bar sits at zero — the same shape the per-instrument
 * `cumulativeVolumeDelta` study uses, so the two read on one scale.
 *
 * Buckets whose net is missing are skipped rather than treated as zero: the
 * aggregate reports nothing for a bucket in which no leg traded, and carrying
 * the running total flat through it is the honest rendering.
 */
export function crossExchangeCvdPoints(
  candles: CandleList,
  metric: ChartAnalyticsMetric,
  measure: CvdMeasure
): SeriesPoint[] {
  const column = (measure === 'quote' ? candles.netBuyVolumeQuote : candles.netBuyVolumeBase) ?? []
  const points: SeriesPoint[] = []
  let running = 0
  let baseline: number | null = null
  for (let i = 0; i < candles.ticks.length; i++) {
    const timeSec = numeric(candles.ticks[i])
    const net = numeric(column[i])
    if (timeSec === null || net === null) continue
    running += net
    baseline ??= running
    points.push({ timeSec, values: { [metric]: running - baseline } })
  }
  return points
}
